import React, { useMemo } from "react";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import { renderTrainingResourceAvatar } from "@src/features/contentLibrary/utils";
import { LibraryItemType } from "@src/types.generated";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "@src/features/assistantChat/aiChat.generated";
import { cn } from "@src/lib/utils/cn";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";

export interface ChatThumbnailProps {
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  url?: string | null;
  file?: File;
  size?: number;
  className?: string;
}

export const ChatThumbnail: React.FC<ChatThumbnailProps> = ({
  libraryItemFragment,
  mediaFragment,
  url,
  file,
  size = 24,
  className,
}) => {
  const thumbnail = useMemo(() => {
    // File type
    if (file) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={BORDER_RADIUS_STYLE}
          defaultBackgroundHashKey={file.name}
        />
      );
    }

    // LibraryItem type
    if (libraryItemFragment) {
      const itemType = libraryItemFragment.type;

      if (itemType === LibraryItemType.TrainingResource) {
        return renderTrainingResourceAvatar(libraryItemFragment, {
          size,
          style: BORDER_RADIUS_STYLE,
        });
      } else if (itemType === LibraryItemType.Course) {
        return (
          <ContentAvatar
            size={size}
            contentType="Course"
            style={BORDER_RADIUS_STYLE}
            coverImage={libraryItemFragment.coverImage}
            defaultBackgroundHashKey={libraryItemFragment.id}
          />
        );
      } else {
        // Default for other library item types
        return (
          <ContentAvatar
            size={size}
            contentType="Module"
            style={BORDER_RADIUS_STYLE}
            coverImage={libraryItemFragment.coverImage}
            defaultBackgroundHashKey={libraryItemFragment.id}
          />
        );
      }
    }

    // Media type
    if (mediaFragment) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={BORDER_RADIUS_STYLE}
          media={{
            thumbnailImageUrl: mediaFragment.thumbnailImageUrl,
            mediaUrls: {
              en: mediaFragment.mediaUrls.en,
              myTranslation: mediaFragment.mediaUrls.en, // Fallback
            },
          }}
          defaultBackgroundHashKey={mediaFragment.id}
        />
      );
    }

    if (url) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={BORDER_RADIUS_STYLE}
          media={{
            mediaUrls: {
              en: url,
              myTranslation: url,
            },
          }}
          defaultBackgroundHashKey={url}
        />
      );
    }

    return (
      <FileTextOutlineIcon className={cn("size-4 text-gray-8", className)} />
    );
  }, [libraryItemFragment, mediaFragment, url, size, file]);

  return (
    <div
      className={cn(
        "flex items-center justify-center flex-shrink-0",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {thumbnail}
    </div>
  );
};

const BORDER_RADIUS_STYLE = { borderRadius: 6 };
